import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  ShieldCheck, ShieldAlert, RefreshCw, Search, AlertTriangle, CheckCircle2, Loader2, Building2,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { DashboardShell } from '../components/brand/DashboardShell';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ADMIN_NAV = [
  { icon: Building2, label: 'Admin Dashboard', href: '/admin/dashboard' },
  { icon: ShieldCheck, label: 'Exclusions (OIG-LEIE)', active: true },
];

/**
 * Admin OIG-LEIE exclusion-screening console.
 *
 * Surfaces the live OIG-LEIE cache state (last refresh, row count, source)
 * and aggregate screen counts (clear / flagged / requires_review) populated
 * by the monthly background loop. Admins can:
 *   - Force a re-pull of the LEIE CSV from oig.hhs.gov
 *   - Force a re-screen sweep of all clinicians
 *
 * Per the credentialing audit ceiling, this is the "human gate" surface
 * over the auto-screening guardrail: flagged clinicians appear here for
 * human review, never auto-credentialed.
 */
export default function AdminExclusionScreening() {
  const { getAuthHeader } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [sweeping, setSweeping] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/compliance/exclusions/status`, getAuthHeader());
      setStatus(data);
    } catch (e) {
      toast.error('Could not load exclusion status');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const runRefresh = async () => {
    setRefreshing(true);
    try {
      const { data } = await axios.post(`${API}/admin/compliance/exclusions/refresh`, {}, getAuthHeader());
      toast.success(`LEIE refreshed — ${data.row_count.toLocaleString()} rows (${data.source})`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Refresh failed (proxy timeout is OK — operation continues server-side)');
    } finally {
      setRefreshing(false);
    }
  };

  const runSweep = async () => {
    setSweeping(true);
    try {
      const { data } = await axios.post(`${API}/admin/compliance/exclusions/sweep`, {}, getAuthHeader());
      toast.success(`Sweep done — ${data.clear || 0} clear · ${data.flagged || 0} flagged · ${data.requires_review || 0} review`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sweep failed');
    } finally {
      setSweeping(false);
    }
  };

  const meta = status?.meta || {};
  const counts = status?.counts || { clear: 0, flagged: 0, requires_review: 0 };
  const sourceLabel = meta.source === 'live' ? 'Live · oig.hhs.gov' : (meta.source === 'mock' ? 'Mock (preview)' : '—');

  return (
    <DashboardShell
      navItems={ADMIN_NAV}
      title="OIG-LEIE exclusion screening"
      subtitle="Federal exclusion list (HHS-OIG) — monthly refresh + continuous screen. A flagged provider cannot be matched or paid."
      testId="admin-exclusions-page"
    >
      <div className="cn-card p-5 mb-6 flex flex-wrap items-center justify-between gap-3" data-testid="exclusions-controls">
        <div>
          <p className="text-sm text-cn-text-muted" data-testid="exclusions-meta">
            Last refresh: <span className="font-semibold text-cn-text">{(meta.last_refresh_at || '').slice(0, 19).replace('T', ' ')}</span> ·
            {' '}rows: <span className="font-semibold text-cn-text">{Number(meta.row_count || 0).toLocaleString()}</span> ·
            {' '}source: <span className="font-semibold text-cn-text">{sourceLabel}</span>
          </p>
          <p className="text-xs text-cn-text-muted mt-1">Monthly sweep runs in the background; manual controls below.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={load} disabled={loading} className="cn-btn cn-btn-ghost !py-2" data-testid="exclusions-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh view
          </Button>
          <Button onClick={runRefresh} disabled={refreshing} className="cn-btn cn-btn-ghost !py-2" data-testid="exclusions-refetch-leie">
            {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            Re-pull LEIE
          </Button>
          <Button onClick={runSweep} disabled={sweeping} className="cn-btn cn-btn-primary !py-2" data-testid="exclusions-run-sweep">
            {sweeping ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
            Re-screen all
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="cn-card p-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-cn-primary mx-auto" /></div>
      ) : (
        <div className="grid sm:grid-cols-3 gap-4" data-testid="exclusions-counts">
          <div className="cn-card p-5" data-testid="exclusions-tile-clear">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">Clear</p>
                <p className="font-display font-bold text-3xl text-cn-text mt-1">{counts.clear}</p>
              </div>
              <span className="w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center"><CheckCircle2 className="w-6 h-6" /></span>
            </div>
            <p className="text-xs text-cn-text-muted mt-1">No federal exclusion on record. Eligible for matching & payout.</p>
          </div>
          <div className="cn-card p-5" data-testid="exclusions-tile-review">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">Requires review</p>
                <p className="font-display font-bold text-3xl text-cn-text mt-1">{counts.requires_review}</p>
              </div>
              <span className="w-12 h-12 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center"><AlertTriangle className="w-6 h-6" /></span>
            </div>
            <p className="text-xs text-cn-text-muted mt-1">Name-only match on the LEIE. Not auto-blocked — open the clinician&apos;s case + verify.</p>
          </div>
          <div className="cn-card p-5" data-testid="exclusions-tile-flagged">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">Flagged</p>
                <p className="font-display font-bold text-3xl text-cn-text mt-1">{counts.flagged}</p>
              </div>
              <span className="w-12 h-12 rounded-2xl bg-red-50 text-red-600 flex items-center justify-center"><ShieldAlert className="w-6 h-6" /></span>
            </div>
            <p className="text-xs text-cn-text-muted mt-1">NPI-level match on the LEIE. Matching + payout are auto-suspended.</p>
          </div>
        </div>
      )}
    </DashboardShell>
  );
}
